import AuthPageShell from 'components/auth/AuthPageShell'
import PasswordInput from 'components/common/field/Password'
import UnderLink from 'components/common/UnderLink'
import { WizardStepChangeEventHandler } from 'components/types'
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth'
import { Location } from 'newTypes/location'
import { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FieldChangeEventAnswer } from 'types/general'

import { auth } from '../firebase'
import { i18n } from '../i18n'
import { getFriendlyConfirmResetErrorMessage } from '../utils/loginErrorMessages'

function getParam(searchParams: URLSearchParams, name: string): string | null {
  return searchParams.get(name)
}

const ResetPasswordAction = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const mode = getParam(searchParams, 'mode')
  const oobCode = getParam(searchParams, 'oobCode')

  const [accountEmail, setAccountEmail] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordValid, setPasswordValid] = useState(false)
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    const isResetLink = Boolean(oobCode) && (!mode || mode === 'resetPassword')
    if (!isResetLink || !oobCode) {
      setError(i18n.translate('auth.errorInvalidActionCode'))
      setVerifying(false)
      return
    }

    verifyPasswordResetCode(auth, oobCode)
      .then(email => {
        setAccountEmail(email)
        setError(null)
      })
      .catch(err => {
        setError(getFriendlyConfirmResetErrorMessage(err))
      })
      .finally(() => {
        setVerifying(false)
      })
  }, [mode, oobCode])

  const handleFieldChange = (
    e:
      | ChangeEvent<HTMLInputElement>
      | WizardStepChangeEventHandler
      | FieldChangeEventAnswer
      | Location
  ) => {
    if ('target' in e) {
      const { name, value } = e.target
      if (name === 'password') setPassword(value)
      if (name === 'confirmPassword') setConfirmPassword(value)
    } else if ('name' in e && 'value' in e && typeof e.value === 'string') {
      if (e.name === 'password') setPassword(e.value)
      if (e.name === 'confirmPassword') setConfirmPassword(e.value)
    }
  }

  const passwordsMatch = password.length > 0 && password === confirmPassword
  const isFormValid = passwordValid && passwordsMatch

  const handleSubmit = async () => {
    if (!oobCode || !isFormValid) return
    setLoading(true)
    setError(null)
    try {
      await confirmPasswordReset(auth, oobCode, password)
      setSuccess(true)
      toast.success(i18n.translate('auth.resetPasswordConfirmed'))
      setTimeout(() => {
        navigate('/auth', { replace: true, state: { email: accountEmail } })
      }, 1500)
    } catch (err) {
      setError(getFriendlyConfirmResetErrorMessage(err))
      toast.error(getFriendlyConfirmResetErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => navigate('/auth')

  const title = i18n.translate('auth.resetPasswordActionTitle')
  const subtitle = i18n.translate('auth.resetPasswordActionSubtitle')

  if (verifying) {
    return (
      <AuthPageShell title={title} subtitle={subtitle}>
        <div className="flex min-h-[6rem] items-center justify-center">
          <p className="text-sm text-gray-600">
            {i18n.translate('auth.verifyingLink')}
          </p>
        </div>
      </AuthPageShell>
    )
  }

  if (error && !accountEmail) {
    return (
      <AuthPageShell title={title} subtitle={subtitle}>
        <div className="flex flex-col gap-4">
          <p className="text-center text-sm text-red-500">{error}</p>
          <div className="text-center">
            <UnderLink
              label={i18n.translate('common.resetPassword')}
              redirectTo="/reset-password"
              isUnderline
              className="font-medium text-imovPrimary"
            />
          </div>
          <div className="text-center">
            <UnderLink
              label={i18n.translate('auth.logIn')}
              redirectTo="/auth"
              isUnderline
              className="font-medium text-imovPrimary"
            />
          </div>
        </div>
      </AuthPageShell>
    )
  }

  if (success) {
    return (
      <AuthPageShell title={title} subtitle={subtitle}>
        <div className="flex flex-col gap-3">
          <p className="text-center text-sm text-gray-600">
            {i18n.translate('auth.resetPasswordConfirmed')}
          </p>
          <p className="text-center text-xs text-gray-600">
            {i18n.translate('auth.logIn')}...
          </p>
        </div>
      </AuthPageShell>
    )
  }

  return (
    <AuthPageShell title={title} subtitle={subtitle}>
      <form
        onSubmit={e => {
          e.preventDefault()
          if (!loading && isFormValid) void handleSubmit()
        }}
        className="flex flex-col gap-4 [&>div]:!my-0"
      >
        {accountEmail ? (
          <p className="text-center text-sm text-gray-600">
            {i18n.translate('auth.yourEmail')}: <strong>{accountEmail}</strong>
          </p>
        ) : null}
        <PasswordInput
          name="password"
          label={t('common.createNewPassword')}
          value={password}
          errorMessage={t('messages.passwordRequirements')}
          onChange={handleFieldChange}
          setIsValid={setPasswordValid}
          isValidationRequired
        />
        <PasswordInput
          name="confirmPassword"
          label={i18n.translate('auth.confirmPassword')}
          value={confirmPassword}
          onChange={handleFieldChange}
          setIsValid={() => {}}
          isValidationRequired={false}
        />
        {password.length > 0 &&
        confirmPassword.length > 0 &&
        !passwordsMatch ? (
          <p className="text-xs text-red-500">
            {i18n.translate('auth.errorPasswordMismatch')}
          </p>
        ) : null}
        {error ? <p className="text-sm text-red-500">{error}</p> : null}
        <button
          type="submit"
          disabled={!isFormValid || loading}
          className="mt-1 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-imovPrimary py-3 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark disabled:cursor-not-allowed disabled:bg-imovPrimary/40"
        >
          {loading ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          ) : (
            t('common.createPassword')
          )}
        </button>
        <button
          type="button"
          onClick={handleClose}
          className="w-full py-2 text-center text-sm text-gray-500 transition hover:text-imovSecondary"
        >
          {t('common.cancel')}
        </button>
      </form>
    </AuthPageShell>
  )
}

export default ResetPasswordAction
